import {
  HbrTab,
  HbrTabGroup,
  HbrTabPanel,
} from '@harbor/elements-react-standalone';
import React, { useEffect, useMemo, useState } from 'react';
import { ModelConfig, MODELS } from '../RbacConfig';
import AdminTab from './AdminTab';
import { Button, CircularProgress } from '@mui/material';
import { fetchItems } from '../../../support/queries';
import useModelQuery from '../Hooks/useModelQuery';
import RoleEditDialog from '../RoleEditDialog';
import UserGroupEditDialog from '../UserGroupEditDialog';
import AzureADGroupEditDialog from '../AzureADGroupEditDialog';
import { fetchSalesforceSalesLevels } from '../../../support/slices/salesforceSalesLevelSlice';
import { useAppDispatch } from '../../../support/store';
import { useNavigate, useParams } from 'react-router-dom';
import { ADMIN_URL_USER_ACCESS } from './admin-values';
import NavHistoryTracker from '../../../navigation/NavHistoryTracker';
import { useQueryClient } from '@tanstack/react-query'; //-tech-debt-react-query;
import { useAdminPermissions } from '../Hooks/useAdminPermissions';
import APIClientsAdminPage from '../APIClients/APIClientsAdminPage';

const API_CLIENTS_MODEL: ModelConfig = {
  key: 'apiClients',
  label: 'API Clients',
  endpoint: 'auth/client',
  perms: {
    read: 'api.api-client.read',
    create: 'api.api-client.create',
    update: 'api.api-client.update',
    delete: 'api.api-client.delete',
  },
  columns: [],
  fields: [],
};

const AdminUserAccess = () => {
  const appDispatch = useAppDispatch();
  const navigate = useNavigate();
  const { section } = useParams();
  const queryClient = useQueryClient();

  const {
    isVisibleTab,
    hasPerm,
    isAdminFull,
  } = useAdminPermissions();

  const allUserAccessModels: { [key: string]: ModelConfig } = useMemo(
    () => ({
      users: MODELS.users,
      userGroups: MODELS.userGroups,
      roles: MODELS.roles,
      azureADGroups: MODELS.azureADGroups,
      apiClients: API_CLIENTS_MODEL,
    }),
    [],
  );

  const canViewApiClients = isAdminFull || hasPerm('api.api-client.read');

  const modelKeys = useMemo(
    () =>
      ['users', 'userGroups', 'roles', 'azureADGroups', 'apiClients'].filter((key) => {
        if (key === 'apiClients') {
          return canViewApiClients;
        }

        return isVisibleTab(allUserAccessModels[key]);
      }),
    [allUserAccessModels, canViewApiClients, isVisibleTab],
  );

  const [currentTabKey, setCurrentTabKey] = useState<string>(
    !!section && modelKeys.includes(section) ? section : modelKeys[0],
  );

  useEffect(() => {
    const nextTabKey =
      !!section && modelKeys.includes(section)
        ? section
        : modelKeys[0];

    if (nextTabKey && nextTabKey !== currentTabKey) {
      setCurrentTabKey(nextTabKey);
    }
  }, [section, modelKeys, currentTabKey]);

  const userAccessModels: { [key: string]: ModelConfig } = useMemo(() => {
    const out: { [key: string]: ModelConfig } = {};

    for (const key of modelKeys) {
      out[key] = allUserAccessModels[key];
    }

    return out;
  }, [allUserAccessModels, modelKeys]);

  const usersModelForQuery = userAccessModels.users || MODELS.users;

  const userQuery = useModelQuery(
    usersModelForQuery,
    currentTabKey === 'users' && !!userAccessModels.users,
  );

  const userList = userQuery?.data ?? [];

  const userMap = Object.fromEntries(
    (userList || []).map((u: any) => [u.id, u.email]),
  );

  const usersModel = useMemo(() => {
    if (!userAccessModels.users) return undefined;

    return {
      ...userAccessModels.users,
      columns: userAccessModels.users.columns.map((col) =>
        col.key === 'manager'
          ? {
            ...col,
            render: (row: any) => (row.manager ? userMap[row.manager] || '' : ''),
          }
          : col,
      ),
    };
  }, [userAccessModels.users, userMap]);

  const [refreshingGroups, setRefreshingGroups] = useState(false);

  const handleRefreshGroups = async () => {
    setRefreshingGroups(true);

    try {
      await fetchItems('db/azure-ad-group', { refresh: true });
      await queryClient.invalidateQueries({ queryKey: ['db/azure-ad-group'] });
    } finally {
      setRefreshingGroups(false);
    }
  };

  useEffect(() => {
    appDispatch(fetchSalesforceSalesLevels());
  }, [appDispatch]);

  const azureADGroupsActionButton = (
    <Button
      variant="contained"
      color="primary"
      onClick={handleRefreshGroups}
      disabled={refreshingGroups}
      size="small"
      sx={{ ml: 2, minWidth: 140 }}
    >
      {refreshingGroups ? (
        <>
          <CircularProgress size={18} sx={{ mr: 1 }} />
          Refreshing...
        </>
      ) : (
        'Refresh Groups'
      )}
    </Button>
  );

  const handleTabClick = (key: string) => {
    setCurrentTabKey(key);
    navigate(`${ADMIN_URL_USER_ACCESS}/${key}`);
  };

  if (!modelKeys.length) {
    return (
      <>
        <NavHistoryTracker title="userAccess" />

        <h1 className="mt-0 pb-6 border-b border-gray-15 text-gray-60 font-semibold leading-5">
          User Access
        </h1>
      </>
    );
  }

  return (
    <>
      <NavHistoryTracker title={currentTabKey} />

      <h1 className="mt-0 pb-6 border-b border-gray-15 text-gray-60 font-semibold leading-5">
        User Access
      </h1>

      <HbrTabGroup primary className="my-4">
        {modelKeys.map((key) => (
          <HbrTab
            slot="nav"
            panel={key}
            key={key}
            onClick={() => handleTabClick(key)}
            active={key === currentTabKey}
          >
            {userAccessModels[key].label}
          </HbrTab>
        ))}

        <HbrTabPanel name="users">
          {currentTabKey === 'users' && usersModel && (
            <AdminTab
              model={usersModel}
              canCloneOverride={false}
            />
          )}
        </HbrTabPanel>

        <HbrTabPanel name="userGroups">
          {currentTabKey === 'userGroups' && userAccessModels.userGroups && (
            <AdminTab
              model={userAccessModels.userGroups}
              DialogComponent={UserGroupEditDialog}
              canCloneOverride={true}
            />
          )}
        </HbrTabPanel>

        <HbrTabPanel name="roles">
          {currentTabKey === 'roles' && userAccessModels.roles && (
            <AdminTab
              model={userAccessModels.roles}
              DialogComponent={RoleEditDialog}
              canCloneOverride={true}
            />
          )}
        </HbrTabPanel>

        <HbrTabPanel name="azureADGroups">
          {currentTabKey === 'azureADGroups' && userAccessModels.azureADGroups && (
            <AdminTab
              model={userAccessModels.azureADGroups}
              actionButton={azureADGroupsActionButton}
              DialogComponent={AzureADGroupEditDialog}
              canCloneOverride={false}
            />
          )}
        </HbrTabPanel>

        <HbrTabPanel name="apiClients">
          {currentTabKey === 'apiClients' && canViewApiClients && <APIClientsAdminPage />}
        </HbrTabPanel>
      </HbrTabGroup>
    </>
  );
};

export default AdminUserAccess;